import React from 'react';
import { themr } from 'react-css-themr';
import mapTheme from './mapTheme.scss';
import { memoize } from 'common/utils/Memoizer';
import { mapPoiVisibilityStyles } from 'common/components/core/map/MapStyles';
import MapZoomControl from 'common/components/core/map/MapZoomControl';
import NumberUtils from 'common/utils/NumberUtils';
import LatLngUtils from 'common/utils/LatLngUtils';
import BaseCanvasMap from 'common/components/core/canvas_map/BaseCanvasMap';
import { withErrorBoundary } from 'common/components/core/errors';
import useClient from 'common/hooks/useClient';
import { usePlatformSettings } from 'common/modules/platform-settings';
import ErrorReporter from 'common/utils/ErrorReporter';
import Product from 'common/enums/Product';

class BaseGoogleMap extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            center: {
                lat: 17.152357,
                lng: 77.577157
            },
            zoom: 4,
            options: {
                fullscreenControl: false,
                clickableIcons: false,
                ...props.options
            }
        };

        this.splitChildrenIntoMarkersAndNonMarkers = memoize(
            this.splitChildrenIntoMarkersAndNonMarkers
        );
    }

    onMapLoad = ({ map, maps }) => {
        const { mapStyles, onGoogleApiLoaded } = this.props;
        map?.setOptions({
            scaleControl: true,
            styles: mapStyles ? mapStyles : mapPoiVisibilityStyles,
            disableDefaultUI: true
        });
        this.setState({ map });
        onGoogleApiLoaded && onGoogleApiLoaded({ map, maps });
    };

    isMapLoaded = () => {
        const { map } = this.state;
        return Boolean(map);
    };

    setMapZoom = zoom => {
        this.setState(prevState => {
            prevState.map.setZoom(zoom);
            return {
                map: prevState.map
            };
        });
    };

    splitChildrenIntoMarkersAndNonMarkers = children => {
        var markers = [];
        var nonMarkers = [];
        React.Children.forEach(children, child => {
            if (
                NumberUtils.isNumeric(child?.props.lat) &&
                NumberUtils.isNumeric(child?.props.lng) &&
                !child.props.isNotMarker
            ) {
                markers.push(child);
            } else {
                nonMarkers.push(child);
            }
        });
        return { markers, nonMarkers };
    };

    calculateLatLngFromMousePos = mousePos => {
        const { map } = this.state;
        const { x, y } = mousePos;
        const latLng = LatLngUtils.getPixelToLatLngOffset(undefined, map, {
            x,
            y
        });
        return latLng;
    };

    _onChildMouseMove = (hoverKey, childProps, mousePos) => {
        // If map is not loaded, do not allow this operation
        if (!this.isMapLoaded()) {
            return;
        }

        const { onChildMouseMove } = this.props;
        const latLng = this.calculateLatLngFromMousePos(mousePos);
        onChildMouseMove &&
            onChildMouseMove(hoverKey, childProps, { ...mousePos, ...latLng });
    };

    _onChildMouseUp = (hoverKey, childProps, mousePos) => {
        // If map is not loaded, do not allow this operation
        if (!this.isMapLoaded()) {
            return;
        }

        const { onChildMouseUp } = this.props;
        const latLng = this.calculateLatLngFromMousePos(mousePos);
        onChildMouseUp &&
            onChildMouseUp(hoverKey, childProps, { ...mousePos, ...latLng });
    };

    handleMapChange = ({ bounds, center, ...otherMapProps }) => {
        const { onChange, onMapCenterAndRadiusUpdate } = this.props;
        const { ne } = bounds;
        const radius = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(center),
            new google.maps.LatLng(ne)
        );
        onMapCenterAndRadiusUpdate &&
            onMapCenterAndRadiusUpdate({ center, radius });
        onChange && onChange({ bounds, center, ...otherMapProps });
    };

    render() {
        const {
            theme,
            defaultZoom,
            defaultCenter,
            children,
            isV2UI,
            onChildMouseMove, // eslint-disable-line no-unused-vars
            onChildMouseUp, // eslint-disable-line no-unused-vars,
            disableControlsUI, // disable custom controls on map
            positionControlsToLeft, // update controls position on map
            ...others
        } = this.props;
        if (typeof google === 'undefined') {
            const gmapError = new Error('google is undefined');
            gmapError.name = 'GoogleReferenceError';
            throw gmapError;
        }
        const {
            markers,
            nonMarkers
        } = this.splitChildrenIntoMarkersAndNonMarkers(children);
        return (
            <div className={theme.mapWrap}>
                <BaseCanvasMap
                    {...others}
                    onChange={this.handleMapChange}
                    defaultCenter={
                        defaultCenter ? defaultCenter : this.state.center
                    }
                    defaultZoom={defaultZoom ? defaultZoom : this.state.zoom}
                    options={this.state.options}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={this.onMapLoad}
                    onChildMouseMove={this._onChildMouseMove}
                    onChildMouseUp={this._onChildMouseUp}
                    calculateLatLngFromMousePos={
                        this.calculateLatLngFromMousePos
                    }
                >
                    {markers}
                </BaseCanvasMap>
                <MapZoomControl
                    map={this.state.map}
                    setMapZoom={this.setMapZoom}
                    disableControlsUI={disableControlsUI}
                    positionControlsToLeft={positionControlsToLeft}
                    isV2UI={isV2UI}
                    theme={theme}
                />
                {nonMarkers}
            </div>
        );
    }
}

const BaseGoogleMapWithErrorBoundary = withErrorBoundary({
    minimal: true,
    defaultProps: {
        errorBoundaryTitleKey: 'genericError',
        errorBoundarySubtitleKey: 'errorMessages.adblockerError'
    }
})(themr('BaseGoogleMap', mapTheme)(BaseGoogleMap));

const BaseGoogleMapWithRegionCenter = props => {
    const client = useClient();
    const platformSettings = usePlatformSettings();
    const defaultCenter = React.useMemo(() => {
        const countryCenter = platformSettings.localeSettings.countryCenter.toJS();

        // TODO: remove this hack once the country code validation is added in BE
        if (!countryCenter.lat || !countryCenter.lng) {
            const errorMessage = `Invalid country center for the client ${client.clientId}`;
            ErrorReporter.notify(new Error(errorMessage), {
                errorName: errorMessage,
                product: Product.PLATFORM
            });
            return undefined;
        }
        return countryCenter;
    }, [client]);

    return (
        <BaseGoogleMapWithErrorBoundary
            {...props}
            defaultCenter={defaultCenter}
        />
    );
};

export default BaseGoogleMapWithRegionCenter;
